import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PortalLayout from "@/components/portal/PortalLayout";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function PortalComplaints() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ order_no: "", category: "Product", subject: "", description: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/my/complaints").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/complaints", form);
      toast.success("Complaint submitted");
      setOpen(false);
      setForm({ order_no: "", category: "Product", subject: "", description: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <PortalLayout title="Complaints">
      <div className="mb-4 flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-complaint-btn" className="rounded-full">Launch Complaint</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Complaint</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid gap-4">
              <div><Label>Order Number (optional)</Label><Input value={form.order_no} onChange={(e) => setForm({ ...form, order_no: e.target.value })} data-testid="complaint-order" /></div>
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="complaint-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Product", "Delivery", "Payment", "Installment", "Service", "Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Subject</Label><Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="complaint-subject" /></div>
              <div><Label>Description</Label><Textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="complaint-desc" /></div>
              <Button type="submit" disabled={busy} className="rounded-full" data-testid="submit-complaint-btn">{busy ? "Submitting..." : "Submit"}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground">No complaints filed.</div>
      ) : (
        <div className="grid gap-3">
          {items.map((c) => (
            <div key={c.id} className="rounded-xl border border-border bg-card p-4" data-testid={`complaint-${c.id}`}>
              <div className="flex items-center justify-between"><p className="font-semibold">{c.subject}</p><StatusBadge status={c.status} /></div>
              <p className="mt-1 text-xs text-muted-foreground">{c.complaint_no} · {c.category} {c.order_no ? `· ${c.order_no}` : ""}</p>
              <p className="mt-2 text-sm text-muted-foreground">{c.description}</p>
              {(c.replies || []).map((r, i) => <p key={i} className="mt-2 rounded-lg bg-secondary/60 px-3 py-2 text-sm"><span className="font-semibold">{r.by}:</span> {r.text}</p>)}
            </div>
          ))}
        </div>
      )}
    </PortalLayout>
  );
}
